import React, { useState, useEffect, useCallback } from "react";
import { getSunbedPackageLogs, SunbedPackageLogApiItem } from "../../services/sunbeds";
import { CustomerPackageLogsTable, CustomerPackageLog } from "./CustomerPackageLogsTable";
import { SunbedActionToolbar } from "./SunbedActionToolbar";
import { PaginationFooter } from "./PaginationFooter";
import { useToast } from "../../hooks/useToast";

export const CustomerPackageLogs: React.FC = () => {
  const toast = useToast();
  const [logs, setLogs] = useState<CustomerPackageLog[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);

  useEffect(() => {
    const fetchLogs = async () => {
      setIsLoading(true);
      try {
        const response = await getSunbedPackageLogs();

        const mappedLogs: CustomerPackageLog[] = (response.data || []).map((log: SunbedPackageLogApiItem) => ({
          id: log.id,
          customerName: log.customer || "Unknown Customer",
          actionDescription: log.action_description || "No description available",
          updatedByName: log.updated_by || "Unknown",
          updatedAt: log.updated_at,
        }));

        setLogs(mappedLogs);
      } catch (error) {
        console.error("Failed to fetch customer package logs:", error);
        toast.error("Failed to load package logs. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only fetch once on mount

  // Filter logs based on search term
  const filteredLogs = logs.filter((log) => {
    const searchLower = searchTerm.toLowerCase();
    const matches = (value?: string | null) =>
      String(value ?? "").toLowerCase().includes(searchLower);

    return (
      matches(log.customerName) ||
      matches(log.actionDescription) ||
      matches(log.updatedByName)
    );
  });

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedLogs = filteredLogs.slice(indexOfFirstItem, indexOfLastItem);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="flex flex-col space-y-6 overflow-hidden flex-1 h-full">
      <SunbedActionToolbar
        totalItems={logs.length}
        showingStart={paginatedLogs.length > 0 ? indexOfFirstItem + 1 : 0}
        showingEnd={Math.min(indexOfLastItem, filteredLogs.length)}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search logs..."
      />

      <div className="flex-1 rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
        <CustomerPackageLogsTable
          logs={paginatedLogs}
          isLoading={isLoading}
          indexOfFirstItem={indexOfFirstItem}
        />

        <PaginationFooter
          totalItems={logs.length}
          filteredTotal={filteredLogs.length}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

